import { useBounds } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import { Vector3 } from "three";

import { CAMERA_VIEW } from "./camera.constants.js";
import type { CameraBounds, CameraViewport } from "./camera.types.js";

interface OfficeSceneBoundsReaderProps {
  onReady: (bounds: CameraBounds) => void;
}

export const OfficeSceneBoundsReader = ({
  onReady,
}: OfficeSceneBoundsReaderProps) => {
  const bounds = useBounds();
  const hasReportedRef = useRef(false);

  // Runs after every render but only fires once.
  // Parent Bounds.useLayoutEffect (which calls refresh() and populates the Box3) runs
  // before any child useEffect, guaranteeing the box is non-empty when we read it.
  useEffect(() => {
    if (hasReportedRef.current) {
      return;
    }

    const { box } = bounds.getSize();

    if (box.isEmpty()) {
      return;
    }

    hasReportedRef.current = true;

    const boxCenter = new Vector3();
    box.getCenter(boxCenter);
    const boxSize = new Vector3();
    box.getSize(boxSize);

    onReady({
      center: [boxCenter.x, boxCenter.y, boxCenter.z],
      max: [box.max.x, box.max.y, box.max.z],
      min: [box.min.x, box.min.y, box.min.z],
      size: [boxSize.x, boxSize.y, boxSize.z],
    });
  });

  return null;
};

interface OfficeCameraViewportReaderProps {
  onReady: (viewport: CameraViewport) => void;
}

export const OfficeCameraViewportReader = ({
  onReady,
}: OfficeCameraViewportReaderProps) => {
  const size = useThree((state) => state.size);

  useEffect(() => {
    const viewportWidth = Math.max(size.width, CAMERA_VIEW.minViewportSize);
    const viewportHeight = Math.max(size.height, CAMERA_VIEW.minViewportSize);

    onReady({
      aspect: viewportWidth / viewportHeight,
      height: viewportHeight,
      width: viewportWidth,
    });
  }, [onReady, size.height, size.width]);

  return null;
};
